import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatNumber } from '@/lib/format'
import { useCountUp } from '@/hooks/use-count-up'
import { SectionHeading } from '@/components/generals/section-heading'

type Period = 'jaar' | 'basisschool'

const SCHOOL_DAYS = 200
const WEEKS = 40
const CLASS_MINUTES = 12
const HOME_MINUTES = 10
const START_GROEPEN = [4, 5, 6, 7, 8]

interface ResultProps {
    value: number
    label: string
    detail: string
}

const Result: FC<ResultProps> = ({ value, label, detail }): ReactNode => {
    const animated = useCountUp(value)

    return (
        <div className='rounded-2xl bg-white p-5 shadow-soft'>
            <p className='font-heading font-soft text-4xl leading-none font-semibold tracking-tight text-ink tabular-nums sm:text-5xl'>
                {formatNumber(animated)}
            </p>

            <p className='mt-2 font-bold text-ink'>
                {label}
            </p>

            <p className='mt-1 text-sm text-muted-foreground'>
                {detail}
            </p>
        </div>
    )
}

export const CalculatorSection: FC = (): ReactNode => {
    const [period, setPeriod] = useState<Period>('jaar')
    const [startGroep, setStartGroep] = useState(4)

    const years = period === 'jaar' ? 1 : 9 - startGroep
    const rows = period === 'jaar'
        ? [{ key: 'jaar', label: 'Schooljaar' }]
        : START_GROEPEN.filter(groep => groep >= startGroep).map(groep => ({ key: `groep-${groep}`, label: `Groep ${groep}` }))
    const texts = SCHOOL_DAYS * 2 * years
    const minutes = SCHOOL_DAYS * (CLASS_MINUTES + HOME_MINUTES) * years
    const hours = Math.floor(minutes / 60)

    return (
        <section id='rekenvoorbeeld' className='relative overflow-hidden bg-sun-soft py-20 sm:py-28'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <div className='grid gap-12 lg:grid-cols-[1fr_1.15fr] lg:gap-16'>
                    <div>
                        <SectionHeading
                            eyebrow='Tel maar op'
                            title={(
                                <>
                                    Tien minuten per dag lijkt weinig. <span className='font-medium text-leaf italic'>Tel maar op.</span>
                                </>
                            )}
                            intro='Elke schooldag een tekst in de klas en een tekst thuis. Kijk wat dat oplevert in één schooljaar, of tot het eind van groep 8.'
                        />

                        <div className='mt-8 grid gap-5'>
                            <div>
                                <p id='periode-label' className='text-sm font-bold text-ink'>
                                    Periode
                                </p>

                                <div role='radiogroup' aria-labelledby='periode-label' className='mt-2 inline-flex rounded-full bg-white p-1 shadow-soft'>
                                    {([['jaar', 'Eén schooljaar'], ['basisschool', 'Tot en met groep 8']] as Array<[Period, string]>).map(([value, label]) => (
                                        <button
                                            key={value}
                                            type='button'
                                            role='radio'
                                            aria-checked={period === value}
                                            onClick={() => setPeriod(value)}
                                            className={cn(
                                                'rounded-full px-4 py-2 text-sm font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf sm:px-5',
                                                period === value ? 'bg-ink text-paper' : 'text-ink/70 hover:text-ink'
                                            )}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className={cn(period === 'jaar' && 'hidden')}>
                                <p id='groep-label' className='text-sm font-bold text-ink'>
                                    Vanaf groep
                                </p>

                                <div role='radiogroup' aria-labelledby='groep-label' className='mt-2 flex flex-wrap gap-2'>
                                    {START_GROEPEN.map(groep => (
                                        <button
                                            key={groep}
                                            type='button'
                                            role='radio'
                                            aria-checked={startGroep === groep}
                                            onClick={() => setStartGroep(groep)}
                                            className={cn(
                                                'grid size-12 place-items-center rounded-2xl text-lg font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf',
                                                startGroep === groep ? 'bg-leaf text-white shadow-soft' : 'bg-white text-ink hover:bg-white/70'
                                            )}
                                        >
                                            {groep}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='rounded-[2rem] bg-white/60 p-5 ring-1 ring-ink/5 sm:p-7'>
                        <div className='grid gap-4 sm:grid-cols-3'>
                            <Result value={texts} label='teksten' detail={`${formatNumber(texts / 2)} in de klas en ${formatNumber(texts / 2)} thuis`} />
                            <Result value={minutes} label='minuten lezen' detail={`Dat is ruim ${formatNumber(hours)} uur`} />
                            <Result value={SCHOOL_DAYS * years} label='leesdagen' detail={years === 1 ? '40 weken van 5 dagen' : `${years} schooljaren van 40 weken`} />
                        </div>

                        <div className='mt-6 rounded-2xl bg-white p-5 shadow-soft'>
                            <p className='text-sm font-bold text-ink'>
                                Elk blokje is een schoolweek: vijf teksten in de klas en vijf thuis.
                            </p>

                            <ul className='mt-4 grid gap-3'>
                                {rows.map((row, rowIndex) => (
                                    <li key={`${period}-${startGroep}-${row.key}`} className='flex items-center gap-3'>
                                        <span className='w-20 shrink-0 text-sm font-semibold text-muted-foreground'>
                                            {row.label}
                                        </span>

                                        <span className='grid flex-1 grid-cols-[repeat(40,minmax(0,1fr))] gap-[2px]' aria-hidden='true'>
                                            {Array.from({ length: WEEKS }, (_, week) => (
                                                <span
                                                    key={week}
                                                    className={cn('cell-in h-5 rounded-[3px]', week % 10 === 9 ? 'bg-leaf-deep' : 'bg-leaf')}
                                                    style={{ animationDelay: `${rowIndex * 120 + week * 12}ms` }}
                                                />
                                            ))}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <details className='group mt-4 rounded-2xl bg-white/70 px-5 py-4'>
                            <summary className='flex cursor-pointer list-none items-center justify-between gap-4 text-sm font-bold text-ink [&::-webkit-details-marker]:hidden'>
                                Hoe is dit berekend?
                                <ChevronDown className='size-4 transition-transform group-open:rotate-180' aria-hidden='true' />
                            </summary>

                            <ul className='mt-3 grid list-disc gap-1.5 pl-5 text-sm leading-relaxed text-muted-foreground'>
                                <li>Rekenvoorbeeld, geen meting.</li>
                                <li>Uitgangspunt: een kind leest op elke schooldag (40 weken van 5 dagen) de tekst in de klas en de extra tekst thuis. Dat is 200 klasteksten en 200 thuisteksten per jaar.</li>
                                <li>Een klastekst is ongeveer 12 minuten lezen, een thuistekst ongeveer 10.</li>
                                <li>Groep 3 rekenen we niet mee: die leest pas vanaf de tweede helft van het schooljaar, met kortere teksten.</li>
                                <li>Voor een heel schooljaar zijn meer themaweken nodig dan er nu klaarstaan (30 tot 47 per groep). Tijdens de pilot komen er steeds bij.</li>
                                <li>Wat kinderen echt lezen, meten we in de pilot.</li>
                            </ul>
                        </details>
                    </div>
                </div>
            </div>
        </section>
    )
}
